import { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Tooltip, Typography } from '@mui/material';
import { fCurrency, fifthDecimalCurrency } from '../../utils/formatNumber';
import { cloudinaryThumbnail, cloudinaryFull } from '../../utils/formatCloudinary';
import Image from '../image';
import Lightbox from '../lightbox';
import CopyBox from './CopyBox';

export default function RenderImage({ params }) {
  RenderImage.propTypes = {
    params: PropTypes.object,
  };

  const { pictures } = params.row;
  const [selectedImage, setSelectedImage] = useState(-1);

  return <>
    <Image
      alt="attachment"
      src={pictures?.length ?
        cloudinaryThumbnail(pictures?.[0]) :
        cloudinaryThumbnail('https://res.cloudinary.com/agoraserv/image/upload/v1738258682/No%20Image%20Animation.gif')
      }
      sx={{
        height: 30,
        cursor: 'pointer',
        '&:hover': {
          opacity: 0.9,
        },
      }}
      onClick={() => setSelectedImage(0)}
    />
    <Lightbox
      index={0}
      slides={pictures?.map((picture) => ({ src: cloudinaryFull(picture) }))}
      open={selectedImage >= 0}
      close={() => setSelectedImage(-1)}
    />
  </>;
}

const category = {
  field: 'category',
  headerName: 'Category',
  type: 'customSelect',
  valueGetter: (params) => params.row.category || '',
  onCellClick: (params) => {
    params.event.stopPropagation();
  },
  width: 115,
  renderCell: (params) => (
    <>
      <Tooltip title={params.row.category || ''}>
        <Typography
          variant="subtitle2"
          sx={{
            border: 1,
            width: '100px',
            borderRadius: '3px',
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            my: '4px',
            py: '6px',
            px: 0.5,
          }}
        >
          <Box
            sx={{
              flexGrow: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap', 
              pr: 2,
            }}
          >
            {params.row.category || ''}
          </Box>
        </Typography>
      </Tooltip>
    </>
  ),
};

const subCategory = {
  field: 'subcategory',
  headerName: 'Subcategory',
  type: 'customSelect',
  valueGetter: (params) => params.row.subcategory || '',
  onCellClick: (params) => params.event.stopPropagation(),
  width: 140,
  renderCell: (params) => params.row.subcategory ? <>
    <Tooltip title={params.row.subcategory}>
      <Typography
        variant="subtitle2"
        sx={{
          border: 1,
          width: '125px',
          borderRadius: '3px',
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          my: '4px',
          py: '6px',
          px: 0.5,
        }}
      >
        <Box
          sx={{
            flexGrow: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            pr: 2,
          }}
        >
          {params.row.subcategory}
        </Box>
      </Typography>
    </Tooltip>
  </> : "",
};

const picture = {
  field: 'picture',
  headerName: 'Image',
  width: 80,
  renderCell: (params) => <RenderImage params={params} />,
  sortable: false,
  filterable: false,
};

const description = {
  field: 'description',
  headerName: 'Description',
  minWidth: 100,
  flex: 1,
  renderCell: (params) => (
    <Box
      component="div"
      sx={{
        fontSize: '0.7rem',
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: 3,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'pre-wrap',
      }}
    >
      {params.value}
    </Box>
  ),
  type: 'customSelect',
};

const price = {
  field: 'price',
  headerName: 'Price',
  width: 120,
  renderCell: (params) =>
    params.row.productUOMs?.length > 0 ? (
      <Box
        component="div"
        sx={{
          wordWrap: 'break-word',
          whiteSpace: 'pre-wrap',
          fontSize: '0.8rem',
        }}
      >
        {params.row.productUOMs?.map((obj, index) => (
          <div key={index}>{`${fCurrency(obj.price)} - ${obj.uomAmount}/${obj.uomType}`}</div>
        ))}
      </Box>
    ) : (
      fCurrency(params.row.price)
    ),
  valueGetter: (params) => params.row.price,
  type: 'number',
  headerAlign: 'left',
  align: 'left',
};

const perItem = {
  field: 'perItem',
  headerName: 'Per Item',
  width: 100,
  renderCell: (params) =>
    params.row.productUOMs?.length > 0 ? (
      <Box
        component="div"
        sx={{
          wordWrap: 'break-word',
          whiteSpace: 'pre-wrap',
          fontSize: '0.8rem',
        }}
      >
        {params.row.productUOMs?.map((obj, index) => (
          <div key={index}>{`${fifthDecimalCurrency(obj.price / obj.uomAmount)}`}</div>
        ))}
      </Box>
    ) : (
      fifthDecimalCurrency(params.row.uomAmount === 0 ? params.row.price / 1 : params.row.price / params.row.uomAmount)
    ),
  type: 'number',
  headerAlign: 'left',
  align: 'left',
};

const uom = {
  field: 'uomType',
  headerName: 'UOM',
  renderCell: (params) => {
    const uomAmount = params.row.uomAmount || '';
    const uomOrType = params.row.uomType || params.row.uom || '';
    return `${uomAmount}/${uomOrType} `;
  },
  valueGetter: (params) => params.row.uomType,
  width: 90,
  type: 'customSelect',
};

const total = {
  field: 'total',
  headerName: 'Total',
  width: 80,
  type: 'customMoney',
  renderCell: (params) => fCurrency(params.row.quantity * params.row.price),
  valueGetter: (params) => params.row.quantity * params.row.price,
};

const vendorName = {
  field: 'vendor',
  headerName: 'Vendor',
  minWidth: 100,
  renderCell: (params) => (
    <Tooltip
      title={params.row.errors?.map((error) => (
        <div key={error}>{error}</div>
      ))}
    >
      <Box
        component="div"
        sx={{
          wordWrap: 'break-word',
          whiteSpace: 'pre-wrap',
          color: params.row.errors?.length > 0 && 'error.main',
        }}
      >
        {params.row.vendor}
      </Box>
    </Tooltip>
  ),
  type: 'customSelect',
};

const vendorItemId = {
  field: 'vendorItemId',
  headerName: 'SKU',
  width: 100,
  renderCell: (params) => <CopyBox text={params.value} />,
  type: 'customSelect',
};

const psNum = {
  field: 'productId',
  headerName: 'PS#',
  width: 90,
  getFilterValues: (row) => [row.productId !== -1 ? `PS${row.productId}` : "Punchout Item"],
  renderCell: (params) => <CopyBox text={params.row.productId !== -1 ? params.value : "Punchout Item"} />,
  valueGetter: (params) => params.row.productId !== -1 ? `PS${params.row.productId}` : "Punchout Item",
  type: 'customSelect',
};

const quantityPlain = { field: 'quantity', headerName: 'Qty', width: 70 };

const CART = [
  picture,
  description,
  subCategory,
  psNum,
  vendorItemId,
  perItem,
  price,
  vendorName,
  total,
];
const CHECKOUT = [
  picture,
  description,
  category,
  subCategory,
  psNum,
  vendorItemId,
  perItem,
  price,
  uom,
  vendorName,
  quantityPlain,
  total,
];
const CHECKOUT_SINGLE_CART = [
  picture,
  description,
  subCategory,
  psNum,
  vendorItemId,
  perItem,
  price,
  uom,
  vendorName,
  quantityPlain,
  total,
];

export { CART, CHECKOUT, CHECKOUT_SINGLE_CART };
